<script>
import date from 'locutus/php/datetime/date'
import AdminLayout from '@layouts/admin-main'
import MemberNav from '@components/admin/member-nav'
import {
  fetchMember,
  createMember,
  updateMember,
  fetchGroupMember,
  deleteGroupMember,
} from '@apis/admin/member'
import { fetchOtps, deleteOtp } from '@apis/admin/otp'
import MemberByGroupInfo from '@components/admin/member-by-group-info'
import MemberAffiliatePolicy from '@components/admin/member-affiliate-policy'
import MemberAcsGrant from '@components/admin/member-acs-grant'

export default {
  page: {
    title: 'Admin::MemberView',
    meta: [{ name: 'description', content: 'Admin::MemberView' }],
  },
  components: {
    AdminLayout,
    MemberNav,
    MemberByGroupInfo,
    MemberAffiliatePolicy,
    MemberAcsGrant,
  },
  data() {
    return {
      loading: false,
      data: {},
      otps: [],
      date,
      groups: [],
      show_group_info: false,
      target_group_seq: 0,
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      this.loading = true
      Promise.all([this.loadData(), this.loadOtps(), this.loadGroups()])
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadData() {
      const { member_info } = await fetchMember(this.$route.params.member_seq)
      this.data = member_info
      this.data.password = ''
    },
    async loadOtps() {
      const { data } = await fetchOtps({
        member_seq: this.$route.params.member_seq,
      })
      this.otps = data
    },
    async loadGroups() {
      const { data } = await fetchGroupMember(this.$route.params.member_seq)
      this.groups = data
    },
    async handleSubmit() {
      try {
        let ret_obj = null
        if (this.data.seq) {
          ret_obj = await updateMember(this.data)
        } else {
          ret_obj = await createMember(this.data)
        }

        this.$notify({
          type: 'success',
          title: this.$t('member_view.notification'),
          message: ret_obj.message,
        })
        this.$router.push({ params: { member_seq: ret_obj.seq } })
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    async handleDeleteOtp(seq) {
      try {
        if (
          !(await this.$confirm(
            this.$t('admin.confirm_delete'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        await deleteOtp(seq)
        await this.loadOtps()
      } catch (e) {
        this.$toasted.error(e.message)
      }
    },
    handleCreateGroupMember() {
      this.show_group_info = true
      this.target_group_seq = null
    },
    async handleDeleteGroupMember(seq) {
      try {
        if (
          !(await this.$confirm(
            this.$t('admin.confirm_delete'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        await deleteGroupMember(this.$route.params.member_seq, seq)
        await this.loadGroups()
      } catch (e) {
        this.$toasted.error(e.message)
      }
    },
    handleGroupMemberSubmit(seq) {
      this.init()
      this.target_group_seq = seq
    },
  },
}
</script>

<template>
  <AdminLayout>
    <MemberNav></MemberNav>
    <h3 :class="$style.xeAdmin">{{ $t('admin_member_list.default') }}</h3>
    <form
      v-loading="loading"
      action="./"
      method="get"
      @submit.prevent="handleSubmit"
    >
      <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
        <colgroup>
          <col width="200" />
          <col width="330" />
          <col width="200" />
          <col width="*" />
        </colgroup>
        <tbody>
          <tr>
            <th scope="col"
              ><div>{{ $t('admin.user_id') }}</div></th
            >
            <td>
              {{ data.user_id }}
              <ElCheckbox
                v-model="data.is_denied"
                :class="$style.fr"
                false-label="N"
                true-label="Y"
                >{{ $t('member_view.denial_of_service') }}</ElCheckbox
              >
            </td>
            <th scope="col"
              ><div>{{ $t('admin.password') }}</div></th
            >
            <td>
              <ElTooltip
                effect="light"
                :content="$t('member_view.existing_password')"
              >
                <input
                  v-model="data.password"
                  type="password"
                  :class="[$style.inputTypeText]"
                />
              </ElTooltip>
            </td>
          </tr>
          <tr>
            <th scope="col"
              ><div>{{ $t('admin.user_name') }}</div></th
            >
            <td
              ><input
                v-model="data.user_name"
                type="user_name"
                :class="[$style.inputTypeText, $style.w120]"
            /></td>
            <th scope="col"
              ><div>{{ $t('admin.email_address') }}</div></th
            >
            <td
              ><input
                v-model="data.email_address"
                type="email_address"
                :class="[$style.inputTypeText]"
            /></td>
          </tr>
          <tr>
            <th scope="col"
              ><div>{{ $t('admin_member_list.regdate') }}</div></th
            >
            <td>{{ date('Y-m-d H:i:s', new Date(data.regdate)) }}</td>
            <th scope="col"
              ><div>{{ $t('admin.last_login') }}</div></th
            >
            <td>{{ date('Y-m-d H:i:s', new Date(data.last_login)) }}</td>
          </tr>
          <tr>
            <th scope="col"
              ><div>{{ $t('member_view.chief_administrator') }}</div></th
            >
            <td>
              <ElCheckbox
                v-model="data.is_admin"
                false-label="N"
                true-label="Y"
                >{{ $t('member_view.authorization') }}</ElCheckbox
              >
            </td>
            <th scope="col"
              ><div>{{ $t('member_view.exclude_statistics') }}</div></th
            >
            <td>
              <ElCheckbox
                v-model="data.use_statistics_exclude"
                false-label="N"
                true-label="Y"
                >{{ $t('member_view.prevention_of_contamination') }}</ElCheckbox
              >
            </td>
          </tr>
          <tr>
            <th scope="col">
              <div>
                {{ $t('member_view.security_rating_value') }}
                <ElCheckbox
                  v-model="data.is_security_level_locked"
                  :class="$style.fr"
                  false-label="N"
                  true-label="Y"
                  >{{ $t('member_view.fixation') }}</ElCheckbox
                >
              </div>
            </th>
            <td colspan="3">
              <input
                v-if="data.is_security_level_locked === 'Y'"
                v-model="data.security_level"
                type="email_address"
                :class="[$style.inputTypeText, $style.tRight, $style.w40]"
              />
              <template v-else>
                {{
                  data.security_level === null
                    ? $t('member_view.unrated')
                    : data.security_level
                }}
              </template>
            </td>
          </tr>
          <tr>
            <th :class="$style.buttonArea" colspan="4">
              <div :class="$style.fr">
                <span :class="[$style.button, $style.black]"
                  ><input
                    type="submit"
                    accesskey="s"
                    :value="$t('member_view.registration')"
                /></span>
              </div>
            </th>
          </tr>
        </tbody>
      </table>
    </form>
    <div :class="$style.adminLeftContent">
      <h3 :class="$style.xeAdmin">{{ $t('member_view.assigned_groups') }}</h3>
      <table cellspacing="0" cellpadding="0" :class="$style.crossTable">
        <thead>
          <tr>
            <th scope="col"
              ><div>{{ $t('member_view.group_name') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('member_view.group_title') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('admin.regdate') }}</div></th
            >
            <th scope="col">
              <div :class="$style.tRight">
                <a
                  href="#"
                  :class="[$style.button, $style.black]"
                  @click.prevent="handleCreateGroupMember"
                  ><span>{{ $t('admin.add') }}</span></a
                >
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="group in groups" :key="group.seq">
            <td :class="[$style.tCenter, $style.nowrap]">{{
              group.group_name
            }}</td>
            <td :class="[$style.wide]">{{ group.group_title }}</td>
            <td :class="[$style.tCenter, $style.nowrap]">{{
              date('Y-m-d H:i:s', new Date(group.regdate))
            }}</td>
            <td :class="[$style.tRight, $style.nowrap]">
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="handleDeleteGroupMember(group.seq)"
                ><span>{{ $t('admin.delete') }}</span></a
              >
            </td>
          </tr>
          <tr v-if="!groups.length">
            <td :colspan="5" :class="$style.tCenter">{{
              $t('member_view.msg_no_group')
            }}</td>
          </tr>
        </tbody>
      </table>
      <MemberAffiliatePolicy
        :member_seq="$route.params.member_seq"
      ></MemberAffiliatePolicy>
    </div>
    <div :class="$style.adminRightExtra">
      <h3 :class="$style.xeAdmin">{{ $t('member_view.otp_list') }}</h3>
      <table cellspacing="0" cellpadding="0" :class="$style.crossTable">
        <thead>
          <tr>
            <th scope="col"
              ><div>{{ $t('member_view.registration_date') }}</div></th
            >
            <th scope="col">&nbsp;</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="otp in otps" :key="otp.seq">
            <td :class="[$style.wide]">{{
              date('Y-m-d H:i:s', new Date(otp.regdate))
            }}</td>
            <td :class="[$style.tRight, $style.nowrap]">
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="handleDeleteOtp(otp.seq)"
                ><span>{{ $t('admin.delete') }}</span></a
              >
            </td>
          </tr>
          <tr v-if="!otps.length">
            <td :colspan="4" :class="$style.tCenter">{{
              $t('member_view.msg_no_otp')
            }}</td>
          </tr>
        </tbody>
      </table>
      <MemberAcsGrant :member_seq="$route.params.member_seq"></MemberAcsGrant>
    </div>
    <ElDialog
      :title="$t('member_view.membership_group_registration')"
      :visible.sync="show_group_info"
      width="1240px"
    >
      <MemberByGroupInfo
        v-if="show_group_info"
        :key="target_group_seq"
        :group_seq="target_group_seq"
        @submit="handleGroupMemberSubmit"
      ></MemberByGroupInfo>
    </ElDialog>
  </AdminLayout>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';
</style>
